// =============================================================
//  MayaIcon — Icône IA "maison" animée pour Maya
//  Un cercle dégradé avec une icône d'étincelles (sparkles),
//  un halo qui pulse et une petite étoile qui scintille.
//  Réutilisable : bouton flottant + en-tête du chat.
//  Synchronisé entre la version mobile et la version web.
// =============================================================

import React, { useEffect, useRef } from 'react';
import { View, StyleSheet, Animated, Easing } from 'react-native';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';

export default function MayaIcon({
  size = 56,
  animated = true,
  colors = ['#165ca2', '#1976D2'],
}) {
  // Halo qui pulse (scale + opacité)
  const pulse = useRef(new Animated.Value(0)).current;
  // Petite étoile qui scintille (opacité + rotation)
  const twinkle = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    if (!animated) return undefined;

    const pulseLoop = Animated.loop(
      Animated.sequence([
        Animated.timing(pulse, {
          toValue: 1,
          duration: 1400,
          easing: Easing.out(Easing.ease),
          useNativeDriver: true,
        }),
        Animated.timing(pulse, {
          toValue: 0,
          duration: 0,
          useNativeDriver: true,
        }),
      ])
    );

    const twinkleLoop = Animated.loop(
      Animated.sequence([
        Animated.timing(twinkle, {
          toValue: 1,
          duration: 900,
          easing: Easing.inOut(Easing.ease),
          useNativeDriver: true,
        }),
        Animated.timing(twinkle, {
          toValue: 0,
          duration: 900,
          easing: Easing.inOut(Easing.ease),
          useNativeDriver: true,
        }),
      ])
    );

    pulseLoop.start();
    twinkleLoop.start();

    return () => {
      pulseLoop.stop();
      twinkleLoop.stop();
    };
  }, [animated, pulse, twinkle]);

  const haloScale = pulse.interpolate({
    inputRange: [0, 1],
    outputRange: [1, 1.8],
  });
  const haloOpacity = pulse.interpolate({
    inputRange: [0, 1],
    outputRange: [0.45, 0],
  });

  const twinkleOpacity = twinkle.interpolate({
    inputRange: [0, 1],
    outputRange: [0.3, 1],
  });
  const twinkleRotate = twinkle.interpolate({
    inputRange: [0, 1],
    outputRange: ['0deg', '90deg'],
  });

  const radius = size / 2;
  const iconSize = size * 0.52;
  const starSize = size * 0.3;

  return (
    <View style={[styles.wrapper, { width: size, height: size }]}>
      {/* Halo pulsant derrière le bouton */}
      {animated && (
        <Animated.View
          style={[
            styles.halo,
            {
              width: size,
              height: size,
              borderRadius: radius,
              backgroundColor: colors[0],
              opacity: haloOpacity,
              transform: [{ scale: haloScale }],
            },
          ]}
        />
      )}

      {/* Cercle dégradé principal */}
      <LinearGradient
        colors={colors}
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 1 }}
        style={[styles.circle, { width: size, height: size, borderRadius: radius }]}
      >
        <MaterialCommunityIcons name="robot-happy" size={iconSize} color="#fff" />
      </LinearGradient>

      {/* Étoile scintillante en haut à droite */}
      <Animated.View
        style={[
          styles.star,
          {
            opacity: twinkleOpacity,
            transform: [{ rotate: twinkleRotate }],
          },
        ]}
      >
        <MaterialCommunityIcons name="star-four-points" size={starSize} color="#FFD54F" />
      </Animated.View>
    </View>
  );
}

const styles = StyleSheet.create({
  wrapper: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  halo: {
    position: 'absolute',
  },
  circle: {
    alignItems: 'center',
    justifyContent: 'center',
    elevation: 6,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 3 },
    shadowOpacity: 0.27,
    shadowRadius: 4.65,
  },
  star: {
    position: 'absolute',
    top: -2,
    right: -2,
  },
});
